import logger from '~/services/logger';

export const ALERT_MODAL_EVENT = 'alert-modal:show';

export type AlertModalDetail = {
  title: string;
  message: string;
  primaryButtonLabel: string;
  secondaryButtonLabel: string;
};

export type AlertModalRequest = AlertModalDetail & {
  resolve: (confirmed: boolean) => void;
};

type AlertModalListener = (request: AlertModalRequest) => void;

const alertModalListeners = new Set<AlertModalListener>();

/**
 * Returns whether a value is valid Alert Modal content.
 */
export function isAlertModalDetail(
  detail: unknown
): detail is AlertModalDetail {
  if (typeof detail !== 'object' || detail === null) {
    return false;
  }

  const candidate = detail as AlertModalDetail;

  return (
    typeof candidate.title === 'string' &&
    candidate.title.length > 0 &&
    typeof candidate.message === 'string' &&
    candidate.message.length > 0 &&
    typeof candidate.primaryButtonLabel === 'string' &&
    candidate.primaryButtonLabel.length > 0 &&
    typeof candidate.secondaryButtonLabel === 'string' &&
    candidate.secondaryButtonLabel.length > 0
  );
}

/**
 * Subscribes to Alert Modal open requests.
 *
 * @param listener - Callback invoked when a modal should open.
 * @returns Unsubscribe function.
 */
export function subscribeAlertModal(listener: AlertModalListener): () => void {
  alertModalListeners.add(listener);
  return () => {
    alertModalListeners.delete(listener);
  };
}

/**
 * Clears Alert Modal listeners. For unit tests only.
 */
export function clearAlertModalListenersForTests(): void {
  alertModalListeners.clear();
}

/**
 * Shows a theme-styled Alert Modal and resolves when the user chooses an option.
 *
 * @param detail - Contextual title, message, and button labels.
 * @returns `true` when confirmed, `false` when declined, or `null` when no host is subscribed.
 */
export function showAlertModal(
  detail: AlertModalDetail
): Promise<boolean | null> {
  if (alertModalListeners.size === 0) {
    logger.info('showAlertModal: no alert modal host subscribed');
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const request: AlertModalRequest = {
      ...detail,
      resolve,
    };

    alertModalListeners.forEach((listener) => listener(request));
  });
}
